import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';

const AddOns = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    api.get('/addons')
      .then((res) => setAddons(res.data || []))
      .catch(() => toast.error('Error al cargar complementos'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return addons.filter((addon) => {
      if (!normalized) return true;
      return addon.name?.toLowerCase().includes(normalized);
    });
  }, [addons, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] ?? '').toString().toLowerCase();
      const bValue = (b[sortKey] ?? '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortDir, sortKey]);

  const toggleSelect = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExport = () => {
    const rows = sorted.filter((addon) => selected[addon.id]);
    if (!rows.length) {
      toast.error('Selecciona al menos un complemento');
      return;
    }
    const header = ['name', 'description', 'price'];
    const csv = [
      header.join(','),
      ...rows.map((a) => [a.name, a.description, a.price].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'complementos.csv';
    link.click();
  };

  return (
    <div data-testid="addons-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Complementos</h1>
          <p className="text-muted-foreground">Servicios adicionales para paquetes</p>
        </div>
        <Button variant="outline" onClick={handleExport}>Exportar CSV</Button>
      </div>
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por nombre"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={`${sortKey}:${sortDir}`}
            onChange={(e) => {
              const [key, dir] = e.target.value.split(':');
              setSortKey(key);
              setSortDir(dir);
            }}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="name:asc">Nombre A-Z</option>
            <option value="name:desc">Nombre Z-A</option>
            <option value="price:asc">Precio menor</option>
            <option value="price:desc">Precio mayor</option>
          </select>
        </CardContent>
      </Card>

      {loading && <p className="text-muted-foreground">Cargando complementos...</p>}
      {!loading && sorted.length === 0 && <p className="text-muted-foreground">No hay complementos.</p>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {sorted.map((addon) => (
          <Card key={addon.id} className="hover:border-primary/50 hover:shadow-md transition-all">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-xl font-heading font-bold mb-2">{addon.name}</h3>
                  <p className="text-muted-foreground mb-4">{addon.description}</p>
                </div>
                <input
                  type="checkbox"
                  checked={Boolean(selected[addon.id])}
                  onChange={() => toggleSelect(addon.id)}
                />
              </div>
              <div className="text-2xl font-heading font-bold text-primary">{addon.price} EUR</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
export default AddOns;
